import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { priceFormatter } from 'common/helpers/format.helper';

interface ISalesTrendLineProps {
  data: number[];
  height?: number;
}

const TOOLTIP_HEIGHT = 22;
const TOOLTIP_PAD_X = 10;
const TOOLTIP_GAP = 8;

export const SalesTrendLine: FC<ISalesTrendLineProps> = ({ data, height = 32 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipTextRef = useRef<SVGTextElement>(null);
  const [width, setWidth] = useState(100);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [tooltipWidth, setTooltipWidth] = useState(TOOLTIP_PAD_X * 2);

  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setWidth(w);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (activeIndex === null) {
      return;
    }

    const el = tooltipTextRef.current;
    if (!el) {
      return;
    }

    setTooltipWidth(el.getBBox().width + TOOLTIP_PAD_X * 2);
  }, [activeIndex, data]);

  if (!data || data.length < 2) {
    return <svg ref={svgRef} width="100%" height={height} />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const DOT_R = 2.5;
  const padX = DOT_R;
  const padY = DOT_R;
  const toX = (i: number) => padX + (i / (data.length - 1)) * (width - padX * 2);
  const toY = (v: number) => height - padY - ((v - min) / range) * (height - padY * 2);

  const coords = data.map((v, i) => ({ x: toX(i), y: toY(v), value: v }));
  const points = coords.map(({ x, y }) => `${x},${y}`).join(' ');

  const activeCoord = activeIndex !== null ? coords[activeIndex] : null;

  let tooltipX = 0;
  let tooltipY = 0;
  if (activeCoord) {
    const maxLeft = Math.max(width - tooltipWidth, 0);
    let left = activeCoord.x + TOOLTIP_GAP;
    if (left + tooltipWidth > width) {
      left = activeCoord.x - TOOLTIP_GAP - tooltipWidth;
    }
    tooltipX = Math.min(Math.max(left, 0), maxLeft);

    const maxTop = Math.max(height - TOOLTIP_HEIGHT, 0);
    let top = activeCoord.y - TOOLTIP_GAP - TOOLTIP_HEIGHT;
    if (top < 0) {
      top = activeCoord.y + TOOLTIP_GAP;
    }
    tooltipY = Math.min(Math.max(top, 0), maxTop);
  }

  return (
    <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        points={points}
        fill="none"
        stroke="#3359ff"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map(({ x, y }, i) => (
        <g
          key={i}
          onMouseEnter={() => setActiveIndex(i)}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <circle
            cx={x}
            cy={y}
            r={activeIndex === i ? 5 : 2.5}
            fill="#3359ff"
            style={{
              transition: 'r .15s ease',
            }}
          />
        </g>
      ))}

      {activeCoord && (
        <g>
          <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={TOOLTIP_HEIGHT} rx={12} fill="#05080F" />

          <text
            ref={tooltipTextRef}
            x={tooltipX + tooltipWidth / 2}
            y={tooltipY + TOOLTIP_HEIGHT / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="14"
            fontFamily="Poppins-Regular, Poppins, sans-serif"
            fill="#ffffff"
          >
            {priceFormatter(activeCoord.value)}
          </text>
        </g>
      )}
    </svg>
  );
};
